import { formatINR, formatDate } from "@/lib/format";
import { printSection } from "@/lib/print";
import { Printer, X } from "lucide-react";

const titles = {
  mediator: "Inward Settlement Voucher",
  direct: "Direct Purchase Slip",
  commission: "Park & Sale Custody Agreement",
  repeat: "Inward Voucher",
};

function Row({ label, value, strong, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", fontSize: 13, borderBottom: "1px dashed #E2E8F0" }}>
      <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: strong ? 800 : 700, color: accent || "#0F172A", textAlign: "right" }}>{value}</span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, color: "#94A3B8", margin: "12px 0 4px" }}>
      {children}
    </div>
  );
}

export default function IntakeVoucher({ vehicle, onClose }) {
  if (!vehicle) return null;
  const v = vehicle;
  const paidCash = v.paid_cash || 0;
  const paidBank = v.paid_bank || 0;
  const total = v.total_buying_cost || v.purchase_price || 0;
  const held = Math.max(total - (paidCash + paidBank), 0);
  const commLabel = v.commission_type === "percent"
    ? `${v.agency_margin || 0}%`
    : formatINR(v.agency_margin || 0);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 12px", overflowY: "auto" }}>
      <div className="no-print" onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.55)" }} />
      <div style={{ position: "relative", background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        {/* Controls */}
        <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #E2E8F0" }}>
          <span className="font-head font-extrabold text-lg tracking-tight">{titles[v.channel] || "Voucher"}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="voucher-print" onClick={() => printSection("voucher")} className="a1-btn a1-btn-action !h-10 !text-sm !px-4">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button data-testid="voucher-close" onClick={onClose} className="a1-btn a1-btn-ghost !h-10 !text-sm !px-3">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Printable voucher (80mm POS / A4 friendly) */}
        <div id="print-voucher" style={{ padding: "20px 24px", fontFamily: "Manrope, sans-serif", color: "#0F172A" }}>
          <div style={{ textAlign: "center", borderBottom: "2px solid #1E3A8A", paddingBottom: 10, marginBottom: 8 }}>
            <img src="/logo.png" alt="A1" style={{ width: 54, height: 54, borderRadius: "50%", margin: "0 auto 4px", display: "block" }} />
            <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: 20, fontWeight: 900, color: "#1E3A8A", letterSpacing: "-.5px" }}>A1 CARS AGENCY</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>Used Car Sales &amp; Purchase • Since 1981</div>
            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 6, color: "#EA580C", textTransform: "uppercase", letterSpacing: 0.5 }}>{titles[v.channel] || "Voucher"}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>Date: {formatDate(new Date())}</div>
          </div>

          {/* Vehicle */}
          <SectionTitle>Vehicle</SectionTitle>
          <Row label="Make / Model" value={v.model || "—"} strong />
          <Row label="Reg No" value={v.reg_no || "—"} />
          <Row label="Year / Colour" value={`${v.year || "—"} / ${v.color || "—"}`} />

          {/* MEDIATOR */}
          {v.channel === "mediator" && (
            <>
              <SectionTitle>Mediator</SectionTitle>
              <Row label="Name" value={v.party_name || "—"} strong />
              <Row label="Mobile" value={v.party_mobile || "—"} />
              {v.party_city ? <Row label="City" value={v.party_city} /> : null}
              <SectionTitle>Settlement</SectionTitle>
              <Row label="Purchase Price" value={formatINR(total)} strong />
              <Row label="Brokerage / Commission" value={formatINR(v.mediator_commission || 0)} accent="#2563EB" />
              <Row label="Upfront Paid — Cash" value={formatINR(paidCash)} accent="#16A34A" />
              <Row label="Upfront Paid — Bank" value={formatINR(paidBank)} accent="#16A34A" />
              <Row label="Held RC Balance" value={formatINR(held)} strong accent="#EA580C" />
            </>
          )}

          {/* DIRECT */}
          {v.channel === "direct" && (
            <>
              <SectionTitle>Seller</SectionTitle>
              <Row label="Name" value={v.party_name || "—"} strong />
              <Row label="Mobile" value={v.party_mobile || "—"} />
              {v.party_city ? <Row label="City" value={v.party_city} /> : null}
              <SectionTitle>Spot Payment (Paid Today)</SectionTitle>
              <Row label="Agreed Price" value={formatINR(total)} strong />
              <Row label="Paid — Cash" value={formatINR(paidCash)} accent="#16A34A" />
              <Row label="Paid — Bank" value={formatINR(paidBank)} accent="#16A34A" />
              <Row label="Remaining Balance (Payable Later)" value={formatINR(held)} strong accent="#EA580C" />
            </>
          )}

          {/* COMMISSION */}
          {v.channel === "commission" && (
            <>
              <SectionTitle>Owner</SectionTitle>
              <Row label="Name" value={v.party_name || "—"} strong />
              <Row label="Mobile" value={v.party_mobile || "—"} />
              <SectionTitle>Custody Terms</SectionTitle>
              <Row label="Expected Asking Price" value={formatINR(v.customer_expected_price || 0)} strong />
              <Row label="Showroom Commission Margin" value={commLabel} accent="#2563EB" />
              <Row label="Vehicle Intake Condition" value={v.notes || "As inspected on intake"} />
            </>
          )}

          {/* Signatures */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, fontSize: 11, fontWeight: 700 }}>
            <div style={{ borderTop: "1px solid #0F172A", paddingTop: 5, width: "45%", textAlign: "center" }}>
              {v.channel === "commission" ? "Owner Signature" : "Seller / Mediator"}
            </div>
            <div style={{ borderTop: "1px solid #0F172A", paddingTop: 5, width: "45%", textAlign: "center" }}>A1 Cars (Authorised)</div>
          </div>
          <div style={{ textAlign: "center", fontSize: 10, color: "#94A3B8", marginTop: 12 }}>
            Computer generated voucher • A1 Cars Agency
          </div>
        </div>
      </div>
    </div>
  );
}
